import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
} from 'antd';
import { DownloadOutlined, PhoneOutlined } from '@ant-design/icons';
import EChart from '../../components/EChart';
import { getAdminDiagnoses, getAdminStats, getLeads, AdminDiagnosis, AdminStats, Lead } from '../../api/admin';

const GRADE_COLORS: Record<string, string> = { A: '#52c41a', B: '#1677ff', C: '#faad14', D: '#fa8c16', E: '#f5222d' };
const GRADE_LABELS: Record<string, string> = { A: '行业领军', B: '数字化追赶者', C: '数字化起步者', D: '数字化萌芽', E: '数字化未启动' };

function exportCsv(rows: Record<string, unknown>[], filename: string): void {
  const headers = Object.keys(rows[0] ?? {});
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? '')}"`).join(','))].join('\n');
  const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * 管理后台（M8）
 * 仪表盘 + 诊断管理 + 线索池
 */
export default function AdminPage() {
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [diagnoses, setDiagnoses] = useState<AdminDiagnosis[]>([]);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [filters, setFilters] = useState({ industry: '', grade: '', status: '', keyword: '' });
  const [diagnosisLoading, setDiagnosisLoading] = useState(false);
  const [diagPage, setDiagPage] = useState(1);
  const [diagPageSize, setDiagPageSize] = useState(10);
  const [leadPage, setLeadPage] = useState(1);
  const [leadPageSize, setLeadPageSize] = useState(10);

  useEffect(() => {
    getAdminStats().then(setStats).catch(() => undefined);
    getLeads().then(setLeads).catch(() => undefined);
  }, []);

  useEffect(() => {
    setDiagnosisLoading(true);
    getAdminDiagnoses(filters).then(setDiagnoses).finally(() => setDiagnosisLoading(false));
  }, [filters]);

  const pieOption = useMemo(() => {
    const dist = stats?.gradeDistribution ?? {};
    const data = Object.entries(dist).map(([g, v]) => ({ value: v, name: `${g} 级` }));
    return {
      tooltip: { trigger: 'item' },
      legend: { bottom: 0 },
      series: [{ type: 'pie', radius: ['40%', '68%'], itemStyle: { borderRadius: 6 }, data }],
    };
  }, [stats]);

  const trendOption = useMemo(() => {
    const trend = stats?.dailyTrend ?? [];
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 30, right: 20, top: 20, bottom: 30 },
      xAxis: { type: 'category', data: trend.map((t) => t.day) },
      yAxis: { type: 'value', minInterval: 1 },
      series: [{ type: 'line', smooth: true, areaStyle: { opacity: 0.15 }, data: trend.map((t) => t.count) }],
    };
  }, [stats]);

  const diagnosisColumns = [
    { title: '企业名', dataIndex: 'companyName', render: (v: string | null) => v ?? '未填' },
    { title: '行业', dataIndex: 'industry', width: 90 },
    { title: '状态', dataIndex: 'status', width: 90, render: (v: string) => <Tag color={v === 'submitted' ? 'green' : 'default'}>{v === 'submitted' ? '已提交' : '草稿'}</Tag> },
    { title: '总分', dataIndex: 'totalScore', width: 80, render: (v: number | null) => v ?? '—' },
    {
      title: '等级',
      dataIndex: 'grade',
      width: 110,
      render: (v: string | null) => (v ? <Tag color={GRADE_COLORS[v] ?? 'default'}>{v} · {GRADE_LABELS[v]}</Tag> : '—'),
    },
    { title: '提交时间', dataIndex: 'submittedAt', width: 170, render: (v: string | null) => (v ? new Date(v).toLocaleString('zh-CN') : '—') },
  ];

  const leadColumns = [
    { title: '联系人', dataIndex: 'name', width: 100 },
    { title: '手机号', dataIndex: 'phone', width: 140 },
    { title: '企业', dataIndex: 'companyName', render: (v: string | null) => v ?? '—' },
    { title: '咨询内容', dataIndex: 'message', render: (v: string | null) => v ?? '—' },
    { title: '状态', dataIndex: 'status', width: 90, render: (v: string) => <Tag color={v === 'pending' ? 'orange' : 'green'}>{v === 'pending' ? '待跟进' : '已跟进'}</Tag> },
    { title: '时间', dataIndex: 'createdAt', width: 170, render: (v: string) => new Date(v).toLocaleString('zh-CN') },
  ];

  return (
    <div>
      <Row gutter={[12, 12]}>
        <Col xs={12} md={6}>
          <Card><Statistic title="诊断总数" value={stats?.total ?? 0} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="完成率" value={stats?.completionRate ?? 0} suffix="%" /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="已提交" value={stats?.submitted ?? 0} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="注册用户" value={stats?.userCount ?? 0} /></Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} md={12}>
          <Card title="等级分布" size="small"><EChart option={pieOption as never} height={260} /></Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="近 7 天提交趋势" size="small"><EChart option={trendOption as never} height={260} /></Card>
        </Col>
      </Row>

      <Card title="诊断管理" style={{ marginTop: 12 }} size="small">
        <Space style={{ marginBottom: 12 }} wrap>
          <Select
            allowClear placeholder="行业" style={{ width: 120 }}
            onChange={(v) => setFilters((f) => ({ ...f, industry: v ?? '' }))}
            options={[
              { value: 'domestic_ecom', label: '国内电商' },
              { value: 'cross_border_ecom', label: '跨境电商' },
              { value: 'logistics', label: '物流货代' },
              { value: 'trade_manufacturing', label: '外贸制造' },
              { value: 'other', label: '其他' },
            ]}
          />
          <Select
            allowClear placeholder="等级" style={{ width: 100 }}
            onChange={(v) => setFilters((f) => ({ ...f, grade: v ?? '' }))}
            options={['A', 'B', 'C', 'D', 'E'].map((g) => ({ value: g, label: `${g} 级` }))}
          />
          <Select
            allowClear placeholder="状态" style={{ width: 110 }}
            onChange={(v) => setFilters((f) => ({ ...f, status: v ?? '' }))}
            options={[
              { value: 'submitted', label: '已提交' },
              { value: 'draft', label: '草稿' },
            ]}
          />
          <Input.Search
            placeholder="搜索企业名" allowClear style={{ width: 200 }}
            onSearch={(v) => setFilters((f) => ({ ...f, keyword: v }))}
          />
          <Button
            icon={<DownloadOutlined />}
            onClick={() =>
              exportCsv(
                diagnoses.map((d) => ({
                  企业名: d.companyName ?? '',
                  行业: d.industry ?? '',
                  状态: d.status,
                  总分: d.totalScore ?? '',
                  等级: d.grade ?? '',
                  提交时间: d.submittedAt ?? '',
                })),
                '诊断记录.csv',
              )
            }
          >
            导出 CSV
          </Button>
        </Space>
        <Table
          rowKey="id"
          size="small"
          loading={diagnosisLoading}
          columns={diagnosisColumns}
          dataSource={diagnoses}
          pagination={{
            current: diagPage,
            pageSize: diagPageSize,
            total: diagnoses.length,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showQuickJumper: true,
            onChange: (page, pageSize) => { setDiagPage(page); setDiagPageSize(pageSize); },
          }}
        />
      </Card>

      <Card title={<Space><PhoneOutlined />线索池（预约顾问）</Space>} style={{ marginTop: 12 }} size="small">
        <Table
          rowKey="id"
          size="small"
          columns={leadColumns}
          dataSource={leads}
          pagination={{
            current: leadPage,
            pageSize: leadPageSize,
            total: leads.length,
            showSizeChanger: true,
            pageSizeOptions: [10, 20, 50, 100],
            showQuickJumper: true,
            onChange: (page, pageSize) => { setLeadPage(page); setLeadPageSize(pageSize); },
          }}
        />
      </Card>
    </div>
  );
}
